import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Trophy, Coins, Users } from "lucide-react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";

const LeaderboardSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, "users"));
        const userList = snapshot.docs.map((doc, idx) => ({
          id: doc.id,
          ...doc.data(),
          rank: idx + 1,
        }));
        setUsers(userList);
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  return (
    <SectionWrapper id="leaderboard">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Meet Our <Highlight>Developers</Highlight>
      </motion.h2>
      <Subtitle>
        A growing community of problem-solvers and innovators building together.
      </Subtitle>

      {loading ? (
        <LoadingText>Loading developers...</LoadingText>
      ) : users.length === 0 ? (
        <LoadingText>No developers yet. 🚀</LoadingText>
      ) : (
        <>
          <HorizontalScroll>
            {users.map((dev, index) => (
              <motion.div
                key={dev.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card>
                  <RankBadge rank={dev.rank}>
                    <Trophy size={18} />#{dev.rank}
                  </RankBadge>

                  <ProfileImage
                    src={
                      dev.profilePic ||
                      `https://ui-avatars.com/api/?name=${
                        dev.name?.replace(" ", "+") || "User"
                      }&background=9b5cff&color=fff`
                    }
                    alt={dev.name}
                  />

                  <Name>{dev.name || "Unnamed Developer"}</Name>
                  <Role>{dev.role || "Developer"}</Role>

                  <CoinsInfo>
                    <Coins size={16} color="#c59fff" /> {dev.coins || 0} Coins
                  </CoinsInfo>

                  <Country>{dev.country || "🌍"}</Country>
                  <ViewProfile
                    onClick={() => {
                      navigate("/developers/" + dev.id);
                    }}
                  >
                    View Profile
                  </ViewProfile>
                </Card>
              </motion.div>
            ))}
          </HorizontalScroll>

          <ViewAllButton onClick={() => navigate("/members")}>
            <Users size={18} /> View All Developers
          </ViewAllButton>
        </>
      )}
    </SectionWrapper>
  );
};

export default LeaderboardSection;

/* ---------------- Styled Components ---------------- */

const SectionWrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 100px 20px;
  text-align: center;
  overflow: hidden;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  margin-top: 10px;
  margin-bottom: 40px;
`;

const LoadingText = styled.p`
  color: #c59fff;
  font-size: 1rem;
  margin-top: 20px;
`;

const HorizontalScroll = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding: 10px 10px 30px 10px;
  scroll-behavior: smooth;
  justify-content: flex-start;

  // &::-webkit-scrollbar {
  //   height: 8px;
  // }
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    border-radius: 10px;
  }
`;
const Card = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 30px 20px;
  position: relative;
  transition: 0.3s;
  backdrop-filter: blur(6px);
  min-width: 240px;
  max-width: 240px;
  min-height: 340px; /* ✅ Consistent height */
  max-height: 340px; /* ✅ Prevent tall cards */
  flex-shrink: 0;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 8px 25px rgba(155, 92, 255, 0.3);
    border-color: #c59fff;
  }
`;

const RankBadge = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  background: ${({ rank }) =>
    rank === 1
      ? "linear-gradient(90deg, #ffd700, #f9a602)"
      : rank === 2
      ? "linear-gradient(90deg, #c0c0c0, #e0e0e0)"
      : rank === 3
      ? "linear-gradient(90deg, #cd7f32, #a9623a)"
      : "linear-gradient(90deg, #9b5cff, #c59fff)"};
  padding: 6px 10px;
  border-radius: 8px;
  color: #000;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 20px auto;
  border: 2px solid #c59fff;
  object-fit: cover;
`;

const Name = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 10px;
  color: #fff;
  text-align: center;

  /* ✅ One-line truncation with ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const Role = styled.p`
  font-size: 0.9rem;
  color: #aaa;
  margin: 4px 0 10px;
  text-align: center;

  /* ✅ Truncate if longer than 1 line */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;
const CoinsInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  color: #c59fff;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Country = styled.p`
  color: #d0d0d0;
  font-size: 0.9rem;
  margin-bottom: 15px;
  text-align: center;

  /* ✅ Prevent multi-line wrapping */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const ViewProfile = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  }
`;

const ViewAllButton = styled.button`
  margin-top: 40px;
  background: transparent;
  border: 1px solid #9b5cff;
  color: #c59fff;
  padding: 12px 26px;
  border-radius: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #9b5cff;
    color: #fff;
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.5);
  }
`;
